<script setup>
import { computed, onActivated, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { format } from 'date-fns';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import PlanFeaturesList from './components/PlanFeaturesList.vue';
import PendingPlanSummary from './components/PendingPlanSummary.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAlert } from 'dashboard/composables';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { currentAccount } = useAccount();
const uiFlags = useMapGetter('accounts/getUIFlags');

const billing = computed(() => currentAccount.value?.billing || {});
const customAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);

const planName = computed(() => billing.value.plan_name);
const activePlan = computed(() => billing.value.plan || null);
const hasPlan = computed(() => !!planName.value);
const isPastDue = computed(() => billing.value.status === 'past_due');

const formatPrice = value => {
  if (value == null) return null;

  const rounded = Math.round(Number(value) * 100) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(rounded);
};

const ticketUsage = computed(() => currentAccount.value?.limits?.tickets || {});

const planTicketAllowance = computed(() => {
  if (onTrial.value) {
    return billing.value.trial_limits?.tickets ?? null;
  }

  return (
    activePlan.value?.included_tickets ??
    activePlan.value?.includedTickets ??
    null
  );
});

const ticketMeter = computed(() => {
  const usage = ticketUsage.value;
  const totalFromLimits = usage.total_count ?? usage.totalCount;
  const consumed = usage.consumed ?? 0;
  const total =
    totalFromLimits ??
    (planTicketAllowance.value != null ? planTicketAllowance.value : 0);

  return {
    consumed,
    total,
    overage: usage.overage ?? Math.max(consumed - total, 0),
    periodStart: usage.period_start ?? usage.periodStart,
    periodEnd: usage.period_end ?? usage.periodEnd,
    resetsAt: usage.resets_at ?? usage.resetsAt,
  };
});

const ticketOverageRate = computed(() => {
  const rate =
    activePlan.value?.overage_rate ?? activePlan.value?.overageRate ?? null;
  return rate != null ? formatPrice(rate) : null;
});

const ticketOverageMessage = computed(() => {
  const { overage } = ticketMeter.value;
  if (!overage) return '';

  if (ticketOverageRate.value != null) {
    return t('BILLING_SETTINGS.USAGE.OVERAGE_WITH_RATE', {
      count: overage,
      rate: ticketOverageRate.value,
    });
  }

  return t('BILLING_SETTINGS.USAGE.OVERAGE', { count: overage });
});

const ticketUsagePeriod = computed(() => {
  const { periodStart: start, periodEnd: end } = ticketMeter.value;
  if (!start || !end) return '';

  return t('BILLING_SETTINGS.USAGE.PERIOD', {
    start: format(new Date(start), 'dd MMM yyyy'),
    end: format(new Date(end), 'dd MMM yyyy'),
  });
});

const ticketUsageResetsOn = computed(() => {
  const { resetsAt, periodEnd } = ticketMeter.value;
  const resetDate = resetsAt || periodEnd;
  if (!resetDate) return '';

  return t('BILLING_SETTINGS.USAGE.RESETS_ON', {
    date: format(new Date(resetDate), 'dd MMM yyyy'),
  });
});

const isYearlyPlan = computed(() => billing.value.interval === 'yearly');

const subscriptionRenewalLabel = computed(() =>
  isYearlyPlan.value
    ? t('BILLING_SETTINGS.CURRENT_PLAN.BASE_RENEWS_ON')
    : t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')
);
const subscriptionRenewsOn = computed(() => {
  const renewal = billing.value.renewal_date || customAttributes.value.subscription_ends_on;
  if (!renewal) return '';
  return format(new Date(renewal), 'dd MMM, yyyy');
});

const trialDaysLeft = computed(() => billing.value.trial_days_left || 0);
const onTrial = computed(() => billing.value.on_trial);
const trialLimits = computed(() => billing.value.trial_limits || {});
const pendingPlan = computed(() => billing.value.pending_plan || null);

const pendingPlanEffectiveDate = computed(() => {
  if (!pendingPlan.value?.effective_at) return '';

  return format(new Date(pendingPlan.value.effective_at), 'dd MMM yyyy');
});

const hasPendingPlanChange = computed(
  () => !!pendingPlan.value?.name && !!pendingPlan.value?.effective_at
);

const plansButtonLabel = computed(() => {
  if (!hasPlan.value) return t('BILLING_SETTINGS.PLANS.CHOOSE');
  if (hasPendingPlanChange.value) {
    return t('BILLING_SETTINGS.PLANS.MANAGE_PLANS');
  }

  return t('BILLING_SETTINGS.PLANS.CHANGE_PLAN');
});

const planDescription = computed(() => {
  if (!planName.value) return '';

  const key = `BILLING_SETTINGS.PLANS.PLAN_DESCRIPTIONS.${planName.value.toUpperCase()}`;
  const translated = t(key);
  return translated === key ? '' : translated;
});

const billingCycleLabel = computed(() => {
  if (!hasPlan.value) return '';

  return isYearlyPlan.value
    ? t('BILLING_SETTINGS.CURRENT_PLAN.INTERVAL_YEARLY')
    : t('BILLING_SETTINGS.CURRENT_PLAN.INTERVAL_MONTHLY');
});

const baseFeeLabel = computed(() => {
  if (!activePlan.value?.base_price_monthly) return '';

  if (isYearlyPlan.value && activePlan.value.base_price_yearly) {
    return t('BILLING_SETTINGS.CURRENT_PLAN.BASE_FEE_YEARLY', {
      monthly: formatPrice(activePlan.value.base_price_yearly / 12),
      annual: formatPrice(activePlan.value.base_price_yearly),
    });
  }

  return t('BILLING_SETTINGS.CURRENT_PLAN.BASE_FEE_MONTHLY', {
    amount: formatPrice(activePlan.value.base_price_monthly),
  });
});

const showPlanFeatures = computed(() => hasPlan.value && !!activePlan.value);

const cancelScheduleDialogRef = ref(null);
const isCancelingSchedule = ref(false);

const openCancelScheduleDialog = () => {
  cancelScheduleDialogRef.value?.open();
};

const confirmCancelSchedule = async () => {
  isCancelingSchedule.value = true;
  try {
    await store.dispatch('accounts/cancelScheduledPlanChange');
    cancelScheduleDialogRef.value?.close();
    useAlert(
      t('BILLING_SETTINGS.PLAN_CHANGE.CANCEL_SCHEDULED_SUCCESS', {
        plan: planName.value,
      })
    );
  } catch {
    useAlert(t('BILLING_SETTINGS.PLAN_CHANGE.CANCEL_SCHEDULED_FAILED'));
  } finally {
    isCancelingSchedule.value = false;
  }
};

const goToPlans = () => {
  router.push({ name: 'billing_settings_plans' });
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const refreshTicketLimits = () => store.dispatch('accounts/limits');

onMounted(async () => {
  if (route.query.checkout === 'success') {
    await store.dispatch('accounts/syncCheckout');
  }

  await store.dispatch('accounts/get', { silent: true });

  if (!billing.value.enabled) {
    router.push({ name: 'home' });
    return;
  }

  await refreshTicketLimits();

  if (route.query.checkout === 'success') {
    router.replace({ name: 'billing_settings_index', query: {} });
  }
});

onActivated(() => {
  if (billing.value.enabled) {
    refreshTicketLimits();
  }
});
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetchingItem">
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <section class="flex max-w-4xl flex-col gap-6">
        <div
          v-if="isPastDue"
          class="flex flex-col gap-3 rounded-lg border border-n-amber-6 bg-n-amber-2 p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex flex-col gap-1">
            <p class="text-sm font-semibold text-n-amber-12">
              {{ $t('BILLING_SETTINGS.PAYMENT_PAST_DUE.TITLE') }}
            </p>
            <p class="text-sm text-n-amber-11">
              {{ $t('BILLING_SETTINGS.PAYMENT_PAST_DUE.DESCRIPTION') }}
            </p>
          </div>
          <ButtonV4
            sm
            solid
            blue
            :is-loading="uiFlags.isCheckoutInProcess"
            @click="onClickBillingPortal"
          >
            {{ $t('BILLING_SETTINGS.PAYMENT_PAST_DUE.UPDATE_PAYMENT') }}
          </ButtonV4>
        </div>

        <BillingCard
          :title="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4
              sm
              :solid="!hasPendingPlanChange"
              :blue="!hasPendingPlanChange"
              :faded="hasPendingPlanChange"
              :slate="hasPendingPlanChange"
              @click="goToPlans"
            >
              {{ plansButtonLabel }}
            </ButtonV4>
            <ButtonV4
              v-if="hasPlan"
              sm
              :solid="hasPendingPlanChange"
              :blue="hasPendingPlanChange"
              :faded="!hasPendingPlanChange"
              :slate="!hasPendingPlanChange"
              :is-loading="uiFlags.isCheckoutInProcess"
              @click="onClickBillingPortal"
            >
              {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
            </ButtonV4>
          </template>

          <PendingPlanSummary
            v-if="hasPendingPlanChange"
            class="mb-6"
            :current-plan-name="planName"
            :pending-plan-name="pendingPlan.name"
            :effective-date="pendingPlanEffectiveDate"
            :renewal-date="subscriptionRenewsOn"
            :is-canceling="isCancelingSchedule"
            @cancel="openCancelScheduleDialog"
          />

          <div
            v-else
            class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            <DetailItem
              highlight
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName || $t('BILLING_SETTINGS.PLANS.NO_PLAN')"
            />
            <DetailItem
              v-if="onTrial"
              :label="$t('BILLING_SETTINGS.TRIAL.LABEL')"
              :value="$t('BILLING_SETTINGS.TRIAL.DAYS_LEFT', { count: trialDaysLeft })"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="subscriptionRenewalLabel"
              :value="subscriptionRenewsOn"
            />
          </div>

          <div
            v-if="showPlanFeatures"
            class="flex flex-col gap-5"
            :class="hasPendingPlanChange ? '' : 'mt-6 border-t border-n-weak pt-6'"
          >
            <p
              v-if="planDescription"
              class="text-sm leading-relaxed text-n-slate-11"
            >
              {{ planDescription }}
            </p>

            <div
              class="grid gap-4 rounded-lg border border-n-weak bg-n-alpha-1 p-4 sm:grid-cols-2"
            >
              <DetailItem
                :label="$t('BILLING_SETTINGS.CURRENT_PLAN.BILLING_CYCLE')"
                :value="billingCycleLabel"
              />
              <DetailItem
                v-if="baseFeeLabel"
                :label="$t('BILLING_SETTINGS.CURRENT_PLAN.BASE_FEE')"
                :value="baseFeeLabel"
              />
            </div>

            <div class="flex flex-col gap-3">
              <p
                class="text-xs font-semibold uppercase tracking-wider text-n-slate-11"
              >
                {{
                  hasPendingPlanChange
                    ? $t('BILLING_SETTINGS.CURRENT_PLAN.ACTIVE_FEATURES_HEADING')
                    : $t('BILLING_SETTINGS.PLANS.WHATS_INCLUDED')
                }}
              </p>
              <PlanFeaturesList :plan="activePlan" />
            </div>
          </div>

          <div
            v-else-if="onTrial && trialLimits.tickets"
            class="mt-6 flex flex-col gap-3 border-t border-n-weak pt-6"
          >
            <p
              class="text-xs font-semibold uppercase tracking-wider text-n-slate-11"
            >
              {{ $t('BILLING_SETTINGS.PLANS.WHATS_INCLUDED') }}
            </p>
            <ul class="grid gap-2 sm:grid-cols-2">
              <li class="flex items-start gap-2 text-sm text-n-slate-11">
                <span
                  class="i-lucide-check mt-0.5 size-4 shrink-0 text-n-teal-10"
                  aria-hidden="true"
                />
                <span>
                  {{
                    $t('BILLING_SETTINGS.PLANS.INCLUDED_TICKETS', {
                      count: trialLimits.tickets,
                    })
                  }}
                </span>
              </li>
              <li class="flex items-start gap-2 text-sm text-n-slate-11">
                <span
                  class="i-lucide-check mt-0.5 size-4 shrink-0 text-n-teal-10"
                  aria-hidden="true"
                />
                <span>
                  {{
                    $t('BILLING_SETTINGS.TRIAL.CAPS_SUMMARY', {
                      agents: trialLimits.agents,
                      inboxes: trialLimits.inboxes,
                      tickets: trialLimits.tickets,
                    })
                  }}
                </span>
              </li>
            </ul>
          </div>
        </BillingCard>

        <BillingCard
          v-if="hasPlan || onTrial"
          :title="$t('BILLING_SETTINGS.USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.USAGE.DESCRIPTION')"
        >
          <p
            v-if="ticketUsagePeriod"
            class="mb-4 text-xs text-n-slate-11"
          >
            {{ ticketUsagePeriod }}
          </p>
          <BillingMeter
            :title="$t('BILLING_SETTINGS.USAGE.TICKETS')"
            :consumed="ticketMeter.consumed"
            :total-count="ticketMeter.total"
            :subtitle="ticketUsageResetsOn"
          />
          <p
            v-if="ticketOverageRate && hasPlan && !ticketMeter.overage"
            class="mt-4 text-xs text-n-slate-11"
          >
            {{
              $t('BILLING_SETTINGS.USAGE.OVERAGE_RATE_INFO', {
                rate: ticketOverageRate,
              })
            }}
          </p>
          <p
            v-if="ticketMeter.overage"
            class="mt-4 rounded-md bg-n-amber-3 px-3 py-2 text-xs font-medium text-n-amber-11"
          >
            {{ ticketOverageMessage }}
          </p>
        </BillingCard>

        <BillingHeader
          class="px-1"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        />
      </section>

      <Dialog
        ref="cancelScheduleDialogRef"
        type="alert"
        width="md"
        :title="$t('BILLING_SETTINGS.PLAN_CHANGE.CANCEL_SCHEDULED_TITLE')"
        :description="
          $t('BILLING_SETTINGS.PLAN_CHANGE.CANCEL_SCHEDULED_DESCRIPTION', {
            current: planName,
            plan: pendingPlan?.name,
            date: pendingPlanEffectiveDate,
          })
        "
        :confirm-button-label="
          $t('BILLING_SETTINGS.PLAN_CHANGE.CANCEL_SCHEDULED_CONFIRM', {
            current: planName,
          })
        "
        :is-loading="isCancelingSchedule"
        @confirm="confirmCancelSchedule"
      />
    </template>
  </SettingsLayout>
</template>
